import React, {createContext, useContext, useEffect, useState} from 'react'
import axios from 'axios'
import {DamageCarrier, DefensiveAbility, MonsterIntelligence, MonsterSpecies, MonsterType, MonstrousAbility, OffensiveAbility} from '../../../server/monster'
import {useSession} from './SessionContext'

interface MonsterCatalogContextValue {
    species: MonsterSpecies[]
    types: MonsterType[]
    carriers: DamageCarrier[]
    offensiveAbilities: OffensiveAbility[]
    defensiveAbilities: DefensiveAbility[]
    monstrousAbilities: MonstrousAbility[]
    intelligenceLevels: MonsterIntelligence[]
    refreshSpecies: () => void
    refreshTypes: () => void
    refreshMonstrousAbilities: () => void
}

const MonsterCatalogContext = createContext<MonsterCatalogContextValue>({
    species: [],
    types: [],
    carriers: [],
    offensiveAbilities: [],
    defensiveAbilities: [],
    monstrousAbilities: [],
    intelligenceLevels: [],
    refreshSpecies: () => {},
    refreshTypes: () => {},
    refreshMonstrousAbilities: () => {},
})

export function MonsterCatalogProvider({children}: {children: React.ReactNode}) {
    const {authenticated, loading} = useSession()
    const [species, setSpecies] = useState<MonsterSpecies[]>([])
    const [types, setTypes] = useState<MonsterType[]>([])
    const [carriers, setCarriers] = useState<DamageCarrier[]>([])
    const [offensiveAbilities, setOffensiveAbilities] = useState<OffensiveAbility[]>([])
    const [defensiveAbilities, setDefensiveAbilities] = useState<DefensiveAbility[]>([])
    const [monstrousAbilities, setMonstrousAbilities] = useState<MonstrousAbility[]>([])
    const [intelligenceLevels, setIntelligenceLevels] = useState<MonsterIntelligence[]>([])

    // Refresh functions use a cache-busting param so newly created items bypass
    // the 5-minute Cache-Control set on the server for these endpoints.
    const refreshSpecies = () =>
        axios.get(`/monsters/species?_=${Date.now()}`).then(({data}) => setSpecies(data)).catch(() => {})
    const refreshTypes = () =>
        axios.get(`/monsters/type?_=${Date.now()}`).then(({data}) => setTypes(data)).catch(() => {})
    const refreshMonstrousAbilities = () =>
        axios.get(`/monsters/monstrous-abilities?_=${Date.now()}`).then(({data}) => setMonstrousAbilities(data)).catch(() => {})

    useEffect(() => {
        if (loading || !authenticated) return
        axios.get('/monsters/species').then(({data}) => setSpecies(data)).catch(() => {})
        axios.get('/monsters/type').then(({data}) => setTypes(data)).catch(() => {})
        axios.get('/monsters/carriers').then(({data}) => setCarriers(data)).catch(() => {})
        axios.get('/monsters/abilities').then(({data}) => setOffensiveAbilities(data)).catch(() => {})
        axios.get('/monsters/defensive-abilities').then(({data}) => setDefensiveAbilities(data)).catch(() => {})
        axios.get('/monsters/monstrous-abilities').then(({data}) => setMonstrousAbilities(data)).catch(() => {})
        axios.get('/monsters/intelligence').then(({data}) => setIntelligenceLevels(data)).catch(() => {})
    }, [loading, authenticated])

    return (
        <MonsterCatalogContext.Provider value={{
            species, types, carriers, offensiveAbilities, defensiveAbilities, monstrousAbilities, intelligenceLevels,
            refreshSpecies, refreshTypes, refreshMonstrousAbilities,
        }}>
            {children}
        </MonsterCatalogContext.Provider>
    )
}

export function useMonsterCatalog(): MonsterCatalogContextValue {
    return useContext(MonsterCatalogContext)
}
